/**
 * The native app's own session store (Capacitor) — 2026-10-10.
 *
 * The web keeps its session in HttpOnly cookies the browser attaches. The
 * native build cannot: it runs from `capacitor://localhost` (iOS) or
 * `https://localhost` (Android), where api.pontimed.app's SameSite=Lax
 * cookies are third-party — the WebView neither keeps nor sends them. So the
 * app holds its credentials itself (apps/api lib/native-session.ts):
 *   - a sign-in from the app's origin answers with `session` in its body →
 *     `adoptNativeSession`;
 *   - every API call carries the access token as `Authorization: Bearer`
 *     (configureAuthClient wires `nativeAccessToken` as the bearer resolver);
 *   - /api/auth/refresh and /api/auth/refresh/logout get the refresh token in
 *     the JSON body (`refreshSession`, `signOutThisDevice`), and the rotated
 *     pair comes back in the body — the server applies the same rotation,
 *     reuse and revocation rules as to the cookie;
 *   - the step-up receipts — `pm_2fa` (a passed 2FA challenge) and `pm_vault`
 *     (an unlocked vault) — come in any answer's `receipts` and go back on
 *     every call as `X-PM-2FA` / `X-PM-Vault` (`adoptNativeReceipts`,
 *     `nativeReceiptHeaders`, wired into @pontimed/api-client by
 *     configureAuthClient). The server checks them like the cookies and reads
 *     the headers from the app's origin only. The owner-proof session
 *     (`ownerProof` → `X-Owner-Proof-Session`, the web's `pm_proof` cookie)
 *     travels the same way.
 *
 * Where it is all kept is the app's choice (`NativeSessionStore`): the web
 * build configures none, and then nothing here does anything.
 */

/** A step-up receipt as the app keeps it. */
export interface NativeReceipt {
  /** The signed value — the cookie's. */
  value: string;
  /** Unix ms. */
  expiresAt: number;
}

/** The pair as apps/api hands it to the app (`session` in a sign-in or refresh answer). */
export interface NativeSession {
  accessToken: string;
  refreshToken: string;
  /** Unix seconds. */
  accessTokenExpiresAt?: number;
  /** Unix ms. */
  refreshTokenExpiresAt?: number;
  /** The step-up receipts this session holds (see the header). */
  receipts?: Partial<Record<NativeReceiptKind, NativeReceipt>>;
}

/**
 * The receipts, by their name in an answer's `receipts`. `ownerProof`: the
 * session an owner proof (2FA revoke, unlink, merge, forgotten vault PIN) was
 * started in — apps/api lib/owner-proof.ts, the web's `pm_proof` cookie — so
 * the check finds the proof the start parked.
 */
export type NativeReceiptKind = "twoFactor" | "vaultUnlock" | "ownerProof";

/** The request header each receipt goes back in (apps/api reads these). */
const RECEIPT_HEADERS: Record<NativeReceiptKind, string> = {
  twoFactor: "X-PM-2FA",
  vaultUnlock: "X-PM-Vault",
  ownerProof: "X-Owner-Proof-Session",
};

/** Where the app keeps its pair (secure storage on device; may be async). */
export interface NativeSessionStore {
  read(): NativeSession | null | Promise<NativeSession | null>;
  write(session: NativeSession): void | Promise<void>;
  clear(): void | Promise<void>;
}

let store: NativeSessionStore | null = null;

/** App boot (configureAuthClient): the store, or null for the web build. */
export function configureNativeSession(next: NativeSessionStore | null): void {
  store = next;
}

/** True when this build keeps its own tokens (the native app). */
export function usesNativeSession(): boolean {
  return store !== null;
}

/** A well-formed `session` object, else null. */
function sessionFrom(body: unknown): NativeSession | null {
  if (!body || typeof body !== "object") return null;
  const session = (body as { session?: unknown }).session;
  if (!session || typeof session !== "object") return null;
  const { accessToken, refreshToken, accessTokenExpiresAt, refreshTokenExpiresAt } =
    session as Record<string, unknown>;
  if (typeof accessToken !== "string" || typeof refreshToken !== "string") return null;
  if (accessToken.length === 0 || refreshToken.length === 0) return null;
  return {
    accessToken,
    refreshToken,
    accessTokenExpiresAt:
      typeof accessTokenExpiresAt === "number" ? accessTokenExpiresAt : undefined,
    refreshTokenExpiresAt:
      typeof refreshTokenExpiresAt === "number" ? refreshTokenExpiresAt : undefined,
  };
}

async function read(): Promise<NativeSession | null> {
  if (!store) return null;
  try {
    return await store.read();
  } catch {
    return null;
  }
}

/** The access token to send as `Authorization: Bearer`, or null (web build / signed out). */
export async function nativeAccessToken(): Promise<string | null> {
  return (await read())?.accessToken ?? null;
}

/** The refresh token the app presents to /api/auth/refresh*, or null. */
export async function nativeRefreshToken(): Promise<string | null> {
  return (await read())?.refreshToken ?? null;
}

/**
 * Keep the pair a sign-in or refresh answered with (`session` in its body —
 * apps/api sends it to the app's origin only). Call it with the response
 * body of every sign-in. A sign-in starts without receipts (a passkey
 * sign-in that also passed 2FA brings its own); a refresh — `keepReceipts` —
 * keeps the ones it had. True when a pair was kept; false on the web build or
 * when the answer carried none.
 */
export async function adoptNativeSession(
  body: unknown,
  { keepReceipts = false }: { keepReceipts?: boolean } = {},
): Promise<boolean> {
  if (!store) return false;
  const session = sessionFrom(body);
  if (!session) return false;
  const receipts = keepReceipts ? (await read())?.receipts : undefined;
  await store.write(receipts ? { ...session, receipts } : session);
  await adoptNativeReceipts(body);
  return true;
}

/**
 * Keep (or drop) the receipts an answer hands the app (`receipts` in its
 * body; `null` = ended, e.g. the vault locked). Wired as @pontimed/api-client
 * `onResponseBody`. Nothing to keep them in before a session exists.
 */
export async function adoptNativeReceipts(body: unknown): Promise<void> {
  if (!store || !body || typeof body !== "object") return;
  const handed = (body as { receipts?: unknown }).receipts;
  if (!handed || typeof handed !== "object") return;
  const current = await read();
  if (!current) return;
  const receipts = { ...current.receipts };
  for (const kind of Object.keys(RECEIPT_HEADERS) as NativeReceiptKind[]) {
    if (!(kind in handed)) continue;
    const receipt = (handed as Record<string, unknown>)[kind];
    if (receipt === null) {
      delete receipts[kind];
      continue;
    }
    const { value, expiresInSeconds } = (receipt ?? {}) as Record<string, unknown>;
    if (typeof value === "string" && value && typeof expiresInSeconds === "number") {
      receipts[kind] = { value, expiresAt: Date.now() + expiresInSeconds * 1000 };
    }
  }
  await store.write({ ...current, receipts });
}

/**
 * The receipt headers for a request: every unexpired receipt the session
 * holds. Wired as @pontimed/api-client `getRequestHeaders`.
 */
export async function nativeReceiptHeaders(): Promise<Record<string, string> | null> {
  const receipts = (await read())?.receipts;
  if (!receipts) return null;
  const headers: Record<string, string> = {};
  const now = Date.now();
  for (const kind of Object.keys(RECEIPT_HEADERS) as NativeReceiptKind[]) {
    const receipt = receipts[kind];
    if (receipt && receipt.expiresAt > now) headers[RECEIPT_HEADERS[kind]] = receipt.value;
  }
  return headers;
}

/** Forget the pair and its receipts (signed out, or the server refused it). Never throws. */
export async function clearNativeSession(): Promise<void> {
  if (!store) return;
  try {
    await store.clear();
  } catch {
    /* nothing left to do — the next refresh is refused anyway */
  }
}
